import React, {useMemo} from 'react'
import styled from 'styled-components'
import {
  GIFT_SHOP_RESEND_VIDEO_MAX_BASE_SIZE,
  GIFT_SHOP_RESEND_VIDEO_MAX_DURATION,
} from 'consts'
import {useTranslation} from 'i18n'
import {getFileSize, getVideoDuration, parseDate} from 'utils'
import {Icon, Paragraph} from 'common/components'
import convertUnit from 'lib/unit'
import {GiftShopProfileResendVideoItemBaseProps} from './GiftShopProfileResendVideoProps'

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

const StyledVideoContainer = styled.div`
  width: 100%;
  aspect-ratio: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${({theme}) => theme.white_3};
  position: relative;
`

const StyledVideoIconContainer = styled.div`
  position: absolute;
  align-self: center;
  z-index: 3;
  cursor: pointer;
`

const StyledNoSrc = styled(StyledContainer)`
  position: absolute;
  z-index: 3;
  padding: ${convertUnit(16)};
  gap: ${convertUnit(4)};
  text-align: center;
`

const StyledTitleContainer = styled(StyledContainer)`
  text-align: center;
  gap: ${convertUnit(2)};
  background-color: ${({theme}) => theme.white_2};
  padding: ${convertUnit(12)};
`

const GiftShopProfileResendVideoItemBase = ({
  statePaused,
  videoElement,
  date,
  title,
  duration,
  size,
  hasSource,
  isNewContent,
  onPause,
  onPlay,
}: GiftShopProfileResendVideoItemBaseProps) => {
  const {translate} = useTranslation()
  const [paused, setPaused] = statePaused

  const handleRenderPlayButton = useMemo(
    () =>
      hasSource && (
        <StyledVideoIconContainer
          onClick={() => {
            setPaused((prev) => !prev)
            paused ? onPlay() : onPause()
          }}>
          <Icon
            type={paused ? 'play' : 'pause'}
            size={convertUnit(40)}
            color="white_1"
          />
        </StyledVideoIconContainer>
      ),
    [hasSource, onPause, onPlay, paused, setPaused],
  )

  const handleRenderResendPlaceholder = useMemo(
    () => (
      <StyledNoSrc>
        <Icon type="video-view" size={convertUnit(32)} color="gray_5" />
        <Paragraph fontSize="s" fontWeight="bold" color="gray_5">
          {translate('giftShop:videoResendPlaceholder')}
        </Paragraph>
        <Paragraph fontSize="s" fontWeight="regular" color="gray_5">
          {translate('giftShop:videoResendRequirementSecond', {
            duration: GIFT_SHOP_RESEND_VIDEO_MAX_DURATION,
          })}
        </Paragraph>
        <Paragraph fontSize="s" fontWeight="regular" color="gray_5">
          {translate('giftShop:videoResendRequirementSize', {
            size: GIFT_SHOP_RESEND_VIDEO_MAX_BASE_SIZE,
          })}
        </Paragraph>
        <Paragraph fontSize="s" fontWeight="regular" color="gray_5">
          {translate('giftShop:videoResendRequirementMP4')}
        </Paragraph>
        <Paragraph fontSize="s" fontWeight="regular" color="gray_5">
          {translate('giftShop:videoResendRequirementCodec')}
        </Paragraph>
      </StyledNoSrc>
    ),
    [translate],
  )

  const handleRenderPlaceholder = useMemo(
    () => (!isNewContent || hasSource ? <></> : handleRenderResendPlaceholder),
    [handleRenderResendPlaceholder, hasSource, isNewContent],
  )

  const handleRenderStandardVideo = useMemo(
    () => (
      <>
        {handleRenderPlayButton}
        {handleRenderPlaceholder}
        {videoElement}
      </>
    ),
    [handleRenderPlaceholder, handleRenderPlayButton, videoElement],
  )

  const handleRenderContent = useMemo(
    () => (
      <StyledVideoContainer>{handleRenderStandardVideo}</StyledVideoContainer>
    ),
    [handleRenderStandardVideo],
  )

  const handleRenderTitle = useMemo(
    () => (
      <Paragraph
        fontSize="s"
        fontWeight="bold"
        color={title ? 'primary_5' : 'gray_5'}>
        {title || translate('giftShop:resendBatchNoFileName')}
      </Paragraph>
    ),
    [title, translate],
  )

  const handleRenderDurationSize = useMemo(
    () => (
      <Paragraph fontWeight="medium" color="gray_5">
        {duration && size
          ? `${getVideoDuration(duration, true)} • ${getFileSize(size)}`
          : `${translate('giftShop:videoResendNoDuration')} • ${translate(
              'giftShop:videoResendNoFileSize',
            )}`}
      </Paragraph>
    ),
    [duration, size, translate],
  )

  const handleRenderDate = useMemo(
    () => (
      <Paragraph fontSize="s" color="gray_4">
        {(date && parseDate(date, 'D MMMM, YYYY, hh:mm')) ||
          translate('giftShop:resendBatchNoDate')}
      </Paragraph>
    ),
    [date, translate],
  )

  return (
    <StyledContainer>
      {handleRenderContent}
      <StyledTitleContainer>
        {handleRenderTitle}
        {handleRenderDurationSize}
        {handleRenderDate}
      </StyledTitleContainer>
    </StyledContainer>
  )
}

export default GiftShopProfileResendVideoItemBase
